import { useEffect, useRef, useState } from 'react';
import productService from '../services/api/productService';
import { FetchProgress } from '../types/product';

export type FetchStageKey = 'basic_info' | 'brand_guideline' | 'images';
export type FetchStageStatus = 'pending' | 'completed' | 'completed_with_warning' | 'failed';

export interface FetchStage {
  key: FetchStageKey;
  label: string;
  status: FetchStageStatus;
}

export interface UseProductFetchProgressResult {
  progress: FetchProgress | null;
  status: number | string | null;
  percent: number;
  stages: FetchStage[];
  /** 情報取得の主要ジョブ（基本情報・ブランド・画像）が完了したか。 */
  done: boolean;
}

// fetch_progress の主要3段階を表示用のラベル付きステージへ変換する
// （競合分析はバックエンドで切り離し済みのため表示しない）。
const STAGE_DEFS: Array<{ key: FetchStageKey; label: string }> = [
  { key: 'basic_info', label: '基本情報・戦略マップを取得' },
  { key: 'brand_guideline', label: 'ブランド情報を抽出' },
  { key: 'images', label: '商品画像を取得' },
];

const isTerminal = (status?: FetchStageStatus): boolean =>
  status === 'completed' || status === 'completed_with_warning' || status === 'failed';

const toStageStatus = (value: unknown): FetchStageStatus =>
  value === 'completed' || value === 'completed_with_warning' || value === 'failed' ? value : 'pending';

// 進捗%の時間経過ブレンドの既定パラメータ。
const DEFAULT_TAU_MS = 45000; // 1段階を漸近的に埋めていく時定数（basic_info は数分かかるため長め）
const DEFAULT_APPROACH_RATIO = 0.9; // 現在段階内で次の境界のどこまで近づくか（境界は超えない）

/**
 * 実段階（0/25/50/75/100 の確定進捗）に、現在段階の経過時間に応じた漸近的なシミュレート進捗を
 * ブレンドして 0〜100 の表示%を返す純関数。
 *
 * - 完了段階ぶんは確定進捗（floor）。現在段階内は経過時間で floor→次境界の手前まで漸近的に増える。
 * - 次の境界は超えない（段階完了時に floor が跳ね上がり、そこへスナップする＝単調増加）。
 * - done なら 100。全段階終了だが done でない（status がまだ fetching 等）は 99 で頭打ち。
 */
export function computeBlendedPercent(params: {
  completedCount: number;
  totalStages: number;
  elapsedInStageMs: number;
  done: boolean;
  tauMs?: number;
  approachRatio?: number;
}): number {
  const {
    completedCount,
    totalStages,
    elapsedInStageMs,
    done,
    tauMs = DEFAULT_TAU_MS,
    approachRatio = DEFAULT_APPROACH_RATIO,
  } = params;

  if (done) return 100;
  if (totalStages <= 0) return 0;

  const completed = Math.max(0, Math.min(completedCount, totalStages));
  const floor = (completed / totalStages) * 100;

  // 全段階が完了しているが done ではない（status がまだ fetching 等）→ 99% で頭打ち。
  if (completed >= totalStages) return 99;

  const nextBoundary = ((completed + 1) / totalStages) * 100;
  const span = nextBoundary - floor;
  const t = Math.max(0, elapsedInStageMs);
  const eased = 1 - Math.exp(-t / tauMs); // 0 → 1 に漸近
  const value = floor + span * approachRatio * eased; // span 全体（次の境界）は超えない
  return Math.min(99, Math.round(value));
}

/**
 * Knowledge 商品の URL 情報取得進捗を実 API（fetch-progress）でポーリングするフック。
 * ウィザードの Deep Research 画面（④）と一覧右側の取得中画面（⑥）で共用する。
 */
export const useProductFetchProgress = (
  productId: number | null | undefined,
  options: { enabled?: boolean; intervalMs?: number } = {}
): UseProductFetchProgressResult => {
  const { enabled = true, intervalMs = 2500 } = options;

  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const [status, setStatus] = useState<number | string | null>(null);
  const timerRef = useRef<number | null>(null);
  // 進捗%の時間ブレンド用: 現在段階の開始時刻（＝完了段階数が変わった時刻）と前回の完了段階数。
  const stageStartRef = useRef<number>(Date.now());
  const prevCompletedRef = useRef<number>(-1);
  // 段階が進まない間も%を漸増させるための再レンダー用tick。
  const [, setTick] = useState(0);

  const active = Boolean(enabled && productId);

  useEffect(() => {
    // 対象商品が変わったら状態をリセットする。
    setProgress(null);
    setStatus(null);
    // 進捗ブレンドの起点もリセット（次レンダーで stageStartRef を now に張り直す）。
    stageStartRef.current = Date.now();
    prevCompletedRef.current = -1;
  }, [productId]);

  useEffect(() => {
    if (!active || !productId) {
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await productService.getKnowledgeFetchProgress(productId);
        if (cancelled) return;

        const nextProgress = res?.fetch_progress ?? null;
        setProgress(nextProgress);
        setStatus((res as any)?.status ?? null);
      } catch {
        // ネットワーク断などは次のポーリングで回復を試みる。
      }
    };

    void poll();
    timerRef.current = window.setInterval(poll, intervalMs);

    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, productId, intervalMs]);

  const stages: FetchStage[] = STAGE_DEFS.map(({ key, label }) => ({
    key,
    label,
    status: toStageStatus(progress ? (progress as any)[key] : undefined),
  }));

  const completedCount = stages.filter((stage) => isTerminal(stage.status)).length;

  // 主要3段階すべてが終了（completed/failed）したら完了扱い（競合分析はバックエンドで
  // 切り離し済みのため待たない）。実際の完了確定は status が fetching から外れることで判定する。
  const allTerminal = stages.every((stage) => isTerminal(stage.status));
  // status===4 は fetching。主要ジョブが終わり status が fetching から外れたら完了扱い。
  const notFetching = status !== 4 && status !== '4' && status !== 'fetching';
  // 取得失敗（status=5）は基本情報が pending のまま止まるため、失敗も終了として扱いポーリングを止める。
  const failed = status === 5 || status === '5' || status === 'failed';
  const done = failed || (Boolean(progress) && allTerminal && notFetching);

  // 完了段階数が変わったら現在段階の開始時刻を張り直す（描画中のref更新は決定的で副作用なし）。
  if (completedCount !== prevCompletedRef.current) {
    prevCompletedRef.current = completedCount;
    stageStartRef.current = Date.now();
  }
  // 実段階の確定進捗（0/25/50/75）に、現在段階の経過時間ぶんの漸近進捗をブレンドして徐々に増やす。
  // basic_info が数分かかる間も0%で止まらず滑らかに増える。progress未取得（初回API前）は0。
  const percent = progress
    ? computeBlendedPercent({
        completedCount,
        totalStages: stages.length,
        elapsedInStageMs: Date.now() - stageStartRef.current,
        done,
      })
    : 0;

  // 段階が進まない間も%を漸増させるため、完了までは1秒ごとに再レンダーする。
  useEffect(() => {
    if (!active || done) return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [active, done]);

  // 完了・失敗が確定したらポーリングを止める（親がアンマウントしないケースの保険）。
  useEffect(() => {
    if (done && timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [done]);

  return { progress, status, percent, stages, done };
};

export default useProductFetchProgress;
